import type { ReactElement } from 'react';

import type { OrderDetailsOrder } from '@/types/orderDetailsOrder';

import { Box } from '@/components/Box';
import { Col } from '@/components/Col';
import { DateOrdinal } from '@/components/DateOrdinal';
import { Flex } from '@/components/Flex';
import { Grid } from '@/components/Grid';
import { ORDER_STATUS_MESSAGE_MAPPING } from '@/components/MyOrders/OrderStatusStepper';
import { Typography } from '@/components/Typography';
import type { OrderAppOrderStatus } from '@/graphql/orderApp/api';
import type { FulfillmentFragment } from '@/graphql/saleor/api';
import * as OM from '@/messages/order';

import { useOrderDetailsContext } from '../OrderDetailsContext';

export type OrderMetaSectionProps = Readonly<{
  fulfillment: FulfillmentFragment | undefined;
  isHomeDelivery: boolean;
  isMobileView: boolean;
  order: OrderDetailsOrder;
  returnOrderCreatedDate: string | undefined;
  trackingNumber: string | undefined;
}>;

type OrderMetaBaseProps = Readonly<
  Pick<OrderMetaSectionProps, 'isMobileView' | 'order'>
>;

function OrderMetaBase({ isMobileView, order }: OrderMetaBaseProps) {
  return (
    <>
      <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
        <Grid>
          <Col xs={12}>
            <Typography
              color='secondaryGrey'
              data-test-id='OrderDetailsViewOrderCreationDate'
            >
              {OM.OrderCreationDate}
            </Typography>
          </Col>
          <Col xs={12}>
            <Typography
              data-test-id='OrderDetailsViewOrderCreationDateValue'
              fontWeight='bold'
            >
              <DateOrdinal date={order.created} />
            </Typography>
          </Col>
        </Grid>
      </Box>
      <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
        <Grid>
          <Col xs={12}>
            <Typography
              color='secondaryGrey'
              data-test-id='OrderDetailsViewOrderNumberLabel'
            >
              {OM.OrderNumber}
            </Typography>
          </Col>
          <Col xs={12}>
            <Typography
              data-test-id='OrderDetailsViewOrderNumberValue'
              fontWeight='bold'
            >
              {order.businessId}
            </Typography>
          </Col>
        </Grid>
      </Box>
    </>
  );
}

type FulfillmentMetaProps = Readonly<
  Pick<
    OrderMetaSectionProps,
    'fulfillment' | 'isMobileView' | 'returnOrderCreatedDate'
  >
>;

function FulfillmentMeta({
  fulfillment,
  isMobileView,
  returnOrderCreatedDate,
}: FulfillmentMetaProps) {
  if (!fulfillment) return null;

  return (
    <>
      <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
        <Grid>
          <Col xs={12}>
            <Typography
              color='secondaryGrey'
              data-test-id='OrderDetailsViewReturnDate'
            >
              {OM.ReturnOrderCreationDate}
            </Typography>
          </Col>
          <Col xs={12}>
            <Typography
              data-test-id='OrderDetailsViewReturnOrderNumberValue'
              fontWeight='bold'
            >
              {returnOrderCreatedDate ? (
                <DateOrdinal date={returnOrderCreatedDate} />
              ) : null}
            </Typography>
          </Col>
        </Grid>
      </Box>
      <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
        <Grid>
          <Col xs={12}>
            <Typography
              color='secondaryGrey'
              data-test-id='OrderDetailsViewOrderNumberLabel'
            >
              {OM.ReturnOrderNumber}
            </Typography>
          </Col>
          <Col xs={12}>
            <Typography
              data-test-id='OrderDetailsViewOrderNumberValue'
              fontWeight='bold'
            >
              {fulfillment.returnBid}
            </Typography>
          </Col>
        </Grid>
      </Box>
    </>
  );
}

type OrderStatusMetaProps = Readonly<
  Pick<
    OrderMetaSectionProps,
    'isHomeDelivery' | 'isMobileView' | 'order' | 'trackingNumber'
  >
>;

function OrderStatusMeta({
  isHomeDelivery,
  isMobileView,
  order,
  trackingNumber,
}: OrderStatusMetaProps) {
  return (
    <>
      <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
        <Grid>
          <Col xs={12}>
            <Typography
              color='secondaryGrey'
              data-test-id='OrderDetailsViewOrderStatusLabel'
            >
              {OM.OrderStatus}
            </Typography>
          </Col>
          <Col xs={12}>
            {order.orderStatus ? (
              <Typography
                color='primary'
                data-test-id='OrderDetailsViewOrderStatus'
                fontWeight='bold'
                textTransform='uppercase'
              >
                {order.orderStatus === 'COLLECTED' &&
                order.orderCollectedDate ? (
                  <>
                    {OM.CollectedOrderStatus}{' '}
                    <DateOrdinal date={order.orderCollectedDate} />
                  </>
                ) : (
                  ORDER_STATUS_MESSAGE_MAPPING[
                    order.orderStatus as OrderAppOrderStatus
                  ]
                )}
              </Typography>
            ) : null}
          </Col>
        </Grid>
      </Box>
      {isHomeDelivery ? (
        <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
          <Grid>
            <Col xs={12}>
              <Typography
                color='secondaryGrey'
                data-test-id='OrderDetailsViewTrackingNumberLabel'
              >
                {OM.TrackingNumber}
              </Typography>
            </Col>
            <Col xs={12}>
              <Typography
                data-test-id='OrderDetailsViewTrackingNumberValue'
                fontWeight='bold'
              >
                {trackingNumber ?? '-'}
              </Typography>
            </Col>
          </Grid>
        </Box>
      ) : null}
    </>
  );
}

export function OrderMetaSection(): ReactElement {
  const {
    fulfillment,
    isHomeDelivery,
    isMobileView,
    order,
    returnOrderCreatedDate,
    trackingNumber,
  } = useOrderDetailsContext();

  return (
    <Box backgroundColor='lightGrey' pb={2} pl={2} pr={2}>
      <Flex flexWrap='wrap' justifyContent='space-between'>
        <OrderMetaBase isMobileView={isMobileView} order={order} />
        {fulfillment ? (
          <FulfillmentMeta
            fulfillment={fulfillment}
            isMobileView={isMobileView}
            returnOrderCreatedDate={returnOrderCreatedDate}
          />
        ) : (
          <OrderStatusMeta
            isHomeDelivery={isHomeDelivery}
            isMobileView={isMobileView}
            order={order}
            trackingNumber={trackingNumber}
          />
        )}
      </Flex>
    </Box>
  );
}
